/**
 * Consumer Edutainment Simulator v0.2
 * Goal: Make it crystal clear why YOUR premium goes up when YOU had no claims
 */

export class ConsumerSimulator {
  constructor(container, onUpdate, version = 'v0.2') {
    console.log('🏗️ ConsumerSimulator constructor called with version:', version);
    this.container = container;
    this.onUpdate = onUpdate;
    this.version = version;
    this.isPaused = false;
    this.isRunning = false;
    this.poolMemberPositions = []; // Store static positions for pool dots
    
    // Simulation state
    this.state = {
      you: {
        premium: 1200,
        hasClaimThisYear: false,
        yearsWithoutClaim: 3,
        score: 0,
        level: 1
      },
      pool: {
        totalMembers: 10000,
        totalPremiums: 12000000,
        totalClaims: 10000000,
        lossRatio: 83.3,
        healthStatus: 'healthy' // healthy, warning, critical
      },
      carrier: {
        targetLossRatio: 85,
        profitMargin: 15,
        needsRateIncrease: false,
      gameMode: this.version === 'v0.3',
      scenario: 'normal',
      },
      premiumRate: 100, // Base = 100, shown as percentage
      animation: {
        premiumFlows: [],
        claimFlows: [],
        events: []
      }
    };
    
    this.canvas = null;
    this.ctx = null;
    this.animationId = null;
    
    this.init();
  }

  init() {
    if (!this.container) {
      console.error('Container is null');
      return;
    }
    
    // Create main canvas
    const canvas = document.createElement('canvas');
    canvas.className = 'w-full h-full';
    
    // Set size based on container
    const containerWidth = this.container.clientWidth || 800;
    const containerHeight = this.container.clientHeight || 600;
    canvas.width = containerWidth;
    canvas.height = containerHeight;
    
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    
    if (!this.ctx) {
      console.error('Failed to get canvas context');
      return;
    }
    
    this.container.appendChild(canvas);
    
    // Pre-calculate pool member positions (FIX #1: Stop flickering)
    this.generatePoolMemberPositions();
    
    // Notify initial state
    if (this.onUpdate) {
      this.onUpdate(this.state);
    }
    
    // Start animation
    this.isRunning = true;
    this.animate();

    // For v0.3, add game elements
    if (this.version === 'v0.3') {
      setTimeout(() => {
        this.addGameElements();
      }, 1000);
    }
  }
  
  // FIX #1: Pre-calculate static positions
  generatePoolMemberPositions() {
    this.poolMemberPositions = [];
    const dotCount = 100;
    
    for (let i = 0; i < dotCount; i++) {
      const angle = (i / dotCount) * Math.PI * 2;
      // Add slight randomness but keep it consistent
      const radiusVariation = 0.3 + Math.random() * 0.6; // 0.3 to 0.9
      this.poolMemberPositions.push({
        angle: angle,
        radiusRatio: radiusVariation
      });
    }
  }

  // Trigger a catastrophic event
  triggerEvent(eventType) {
    const events = {
      hurricane: {
        name: 'Hurricane',
        claimAmount: 5000000,
        affectedMembers: 500,
        description: 'Major hurricane causes $5M in claims'
      },
      wildfire: {
        name: 'Wildfire',
        claimAmount: 3000000,
        affectedMembers: 300,
        description: 'Wildfire season causes $3M in claims'
      },
      accident_spike: {
        name: 'Accident Spike',
        claimAmount: 2000000,
        affectedMembers: 800,
        description: 'Bad winter increases accidents by 40%'
      }
    };

    const event = events[eventType];
    if (!event) return;

    // FIX #2: Immutable state update
    const newTotalClaims = this.state.pool.totalClaims + event.claimAmount;
    const newLossRatio = (newTotalClaims / this.state.pool.totalPremiums) * 100;
    
    // Determine new health status
    let newHealthStatus = 'healthy';
    let needsRateIncrease = false;
    
    if (newLossRatio > 100) {
      newHealthStatus = 'critical';
      needsRateIncrease = true;
    } else if (newLossRatio > 90) {
      newHealthStatus = 'warning';
      needsRateIncrease = true;
    }
    
    // Calculate new rate
    let newPremiumRate = this.state.premiumRate;
    let newYourPremium = this.state.you.premium;
    
    if (needsRateIncrease) {
      const excessLoss = newLossRatio - this.state.carrier.targetLossRatio;
      const rateIncrease = Math.max(5, Math.min(30, excessLoss));
      newPremiumRate = 100 + rateIncrease;
      newYourPremium = Math.round(1200 * (newPremiumRate / 100));
    }
    
    // Create new state object (immutable)
    this.state = {
      ...this.state,
      pool: {
        ...this.state.pool,
        totalClaims: newTotalClaims,
        lossRatio: newLossRatio,
        healthStatus: newHealthStatus
      },
      carrier: {
        ...this.state.carrier,
        needsRateIncrease: needsRateIncrease
      },
      you: {
        ...this.state.you,
        premium: newYourPremium
      },
      premiumRate: newPremiumRate,
      animation: {
        ...this.state.animation,
        events: [...this.state.animation.events, {
          type: eventType,
          name: event.name,
          timestamp: Date.now(),
          claimAmount: event.claimAmount
        }]
      }
    };
    
    // Callback to update UI
    if (this.onUpdate) {
      this.onUpdate(this.state);
    }
  }

  reset() {
    // Reset to initial state
    this.state = {
      you: {
        premium: 1200,
        hasClaimThisYear: false,
        yearsWithoutClaim: 3
      },
      pool: {
        totalMembers: 10000,
        totalPremiums: 12000000,
        totalClaims: 10000000,
        lossRatio: 83.3,
        healthStatus: 'healthy'
      },
      carrier: {
        targetLossRatio: 85,
        profitMargin: 15,
        needsRateIncrease: false
      },
      premiumRate: 100,
      animation: {
        premiumFlows: [],
        claimFlows: [],
        events: []
      }
    };
    
    if (this.onUpdate) {
      this.onUpdate(this.state);
    }
  }

  // FIX #3: Add running check to prevent memory leaks
  animate() {
    if (!this.isRunning) {
      return; // Stop if cleanup was called
    }
    
    if (!this.isPaused && this.ctx && this.canvas) {
      this.draw();
    }
    
    this.animationId = requestAnimationFrame(() => this.animate());
  }

  // FIX #4: Add null checks
  draw() {
    if (!this.ctx || !this.canvas) return;
    
    // Clear canvas
    this.ctx.fillStyle = '#000000';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Draw YOU (left side - highlighted)
    this.drawYou();
    
    // Draw THE POOL (center - main visualization)
    this.drawPool();
    
    // Draw premium flows (golden particles) - Skip for now, not implemented
    // this.drawPremiumFlows();
    
    // Draw claim flows (blue particles) - Skip for now, not implemented
    // this.drawClaimFlows();
    
    // Draw labels and amounts
    this.drawLabels();
  }

  // FIX #4: Add null checks
  drawYou() {
    if (!this.ctx || !this.canvas) return;
    
    const x = this.canvas.width * 0.15;
    const y = this.canvas.height * 0.5;
    
    // Draw YOU as highlighted person icon with glow
    this.ctx.shadowColor = '#43e97b';
    this.ctx.shadowBlur = 20;
    this.ctx.fillStyle = '#43e97b';
    this.ctx.beginPath();
    this.ctx.arc(x, y, 25, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.shadowBlur = 0;
    
    // Inner circle
    this.ctx.fillStyle = '#000000';
    this.ctx.beginPath();
    this.ctx.arc(x, y, 15, 0, Math.PI * 2);
    this.ctx.fill();
    
    // Label
    this.ctx.fillStyle = '#ffffff';
    this.ctx.font = 'bold 18px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('YOU', x, y - 50);
    
    // Your premium
    this.ctx.font = 'bold 16px monospace';
    this.ctx.fillStyle = '#43e97b';
    this.ctx.fillText(`$${this.state.you.premium.toLocaleString()}/year`, x, y + 55);
    
    // Arrow pointing to pool
    const arrowStartX = x + 40;
    const arrowEndX = Math.min(x + 100, this.canvas.width * 0.35);
    this.drawArrow(arrowStartX, y, arrowEndX, y, '#ffd700', 3);
  }

  // FIX #4: Add null checks
  drawPool() {
    if (!this.ctx || !this.canvas) return;
    
    const x = this.canvas.width * 0.5;
    const y = this.canvas.height * 0.5;
    const radius = Math.min(this.canvas.width, this.canvas.height) * 0.2;
    
    // Draw pool circle
    const healthColors = {
      healthy: '#4caf50',
      warning: '#ff9800',
      critical: '#f44336'
    };
    
    this.ctx.strokeStyle = healthColors[this.state.pool.healthStatus];
    this.ctx.lineWidth = 4;
    this.ctx.beginPath();
    this.ctx.arc(x, y, radius, 0, Math.PI * 2);
    this.ctx.stroke();
    
    // Pool label
    this.ctx.fillStyle = '#ffffff';
    this.ctx.font = 'bold 18px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('INSURANCE POOL', x, y - radius - 20);
    
    // Pool stats
    this.ctx.font = '14px monospace';
    this.ctx.fillStyle = '#aaaaaa';
    this.ctx.fillText(`${this.state.pool.totalMembers.toLocaleString()} Members`, x, y - 40);
    this.ctx.fillText(`Premiums: $${(this.state.pool.totalPremiums / 1000000).toFixed(1)}M`, x, y - 20);
    this.ctx.fillText(`Claims: $${(this.state.pool.totalClaims / 1000000).toFixed(1)}M`, x, y);
    
    // Loss ratio
    const lossRatioColor = this.state.pool.lossRatio > 100 ? '#f44336' : 
                           this.state.pool.lossRatio > 90 ? '#ff9800' : '#4caf50';
    this.ctx.fillStyle = lossRatioColor;
    this.ctx.font = 'bold 20px monospace';
    this.ctx.fillText(`Loss Ratio: ${this.state.pool.lossRatio.toFixed(1)}%`, x, y + 30);
    
    // Draw representative dots in pool
    this.drawPoolMembers(x, y, radius);
  }

  // FIX #1: Use pre-calculated positions (no flickering)
  drawPoolMembers(centerX, centerY, radius) {
    if (!this.poolMemberPositions || this.poolMemberPositions.length === 0) {
      return;
    }
    
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    
    for (let i = 0; i < this.poolMemberPositions.length; i++) {
      const pos = this.poolMemberPositions[i];
      const dist = pos.radiusRatio * (radius - 20);
      const x = centerX + Math.cos(pos.angle) * dist;
      const y = centerY + Math.sin(pos.angle) * dist;
      
      this.ctx.beginPath();
      this.ctx.arc(x, y, 2, 0, Math.PI * 2);
      this.ctx.fill();
    }
  }

  drawPremiumFlows() {
    // Draw golden particles flowing from YOU to POOL
    // TODO: Implement animated flow
  }

  drawClaimFlows() {
    // Draw blue particles flowing from POOL out (claims)
    // TODO: Implement animated flow
  }

  // FIX #4: Add null checks
  drawLabels() {
    if (!this.ctx || !this.canvas) return;
    
    // Draw premium rate indicator
    const x = this.canvas.width * 0.5;
    const y = this.canvas.height * 0.88;
    
    this.ctx.fillStyle = '#ffffff';
    this.ctx.font = 'bold 14px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('YOUR PREMIUM RATE', x, y);
    
    // Rate meter
    const meterWidth = 400;
    const meterX = x - meterWidth / 2;
    const meterY = y + 10;
    
    // Background bar
    this.ctx.fillStyle = '#333333';
    this.ctx.fillRect(meterX, meterY, meterWidth, 20);
    
    // Current rate indicator
    const ratePosition = meterX + (this.state.premiumRate - 80) * (meterWidth / 40); // 80-120 range
    this.ctx.fillStyle = this.state.premiumRate > 100 ? '#f44336' : '#4caf50';
    this.ctx.beginPath();
    this.ctx.arc(ratePosition, meterY + 10, 12, 0, Math.PI * 2);
    this.ctx.fill();
    
    // Amount
    this.ctx.fillStyle = this.state.premiumRate > 100 ? '#f44336' : '#4caf50';
    this.ctx.font = 'bold 18px monospace';
    this.ctx.fillText(`$${this.state.you.premium}/year`, x, meterY + 50);
    
    if (this.state.premiumRate > 100) {
      const increase = this.state.premiumRate - 100;
      this.ctx.fillStyle = '#f44336';
      this.ctx.font = '14px sans-serif';
      this.ctx.fillText(`↑ +${increase.toFixed(0)}% increase`, x, meterY + 70);
    }
  }

  pause() {
    this.isPaused = true;
  }

  resume() {
    this.isPaused = false;
  }

  // FIX #3 & #4: Proper cleanup with running flag
  cleanup() {
    this.isRunning = false; // Stop animation loop
    
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    
    if (this.container) {
      this.container.innerHTML = '';
    }
    
    // Clear references
    this.canvas = null;
    this.ctx = null;
    this.poolMemberPositions = [];
  }

  getState() {
    return this.state;
  }

  // Helper: Draw arrow (FIX #4: Add null check)
  drawArrow(x1, y1, x2, y2, color, width) {
    if (!this.ctx) return;
    
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = width;
    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.stroke();
    
    // Arrowhead
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const headLength = 10;
    
    this.ctx.fillStyle = color;
    this.ctx.beginPath();
    this.ctx.moveTo(x2, y2);
    this.ctx.lineTo(
      x2 - headLength * Math.cos(angle - Math.PI / 6),
      y2 - headLength * Math.sin(angle - Math.PI / 6)
    );
    this.ctx.lineTo(
      x2 - headLength * Math.cos(angle + Math.PI / 6),
      y2 - headLength * Math.sin(angle + Math.PI / 6)
    );
    this.ctx.closePath();
    this.ctx.fill();
  }
  
  // Remove unused methods to clean up
  drawPremiumFlows() {
    // Reserved for future animation
  }
  
  drawClaimFlows() {
    // Reserved for future animation
  }

  addGameElements() {
    console.log('🎮 v0.3 addGameElements called - game mode activated');

    // Game state is already initialized in constructor
    console.log('🎮 Game Mode:', this.state.gameMode ? 'ON' : 'OFF');
    console.log('🎮 Score:', this.state.you.score);
    console.log('🎮 Level:', this.state.you.level);
    console.log('🎮 Scenario:', this.state.scenario);

    // Add visual game elements to differentiate v0.3
    this.addGameHeader();
    this.addScenarioSelector();

    console.log('🎮 Game UI elements added successfully');
  }

  addGameHeader() {
    console.log('🎯 Adding game header');

    // Find the canvas container and insert the game header right before it
    const canvasContainer = document.querySelector('.bg-black.rounded-xl.overflow-hidden');
    if (!canvasContainer) {
      console.log('🎯 ERROR: Could not find canvas container (.bg-black.rounded-xl)');
      return;
    }

    console.log('🎯 Canvas container found:', canvasContainer);

    // Create game header
    const gameHeader = document.createElement('div');
    gameHeader.id = 'game-header-v0-3';
    gameHeader.className = 'bg-gradient-to-r from-purple-900/20 to-blue-900/20 rounded-lg p-6 mb-6 border border-purple-500/20';
    gameHeader.innerHTML = `
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-3">
          <span class="text-2xl">🎯</span>
          <h3 class="text-xl font-bold text-white">Strategy Game Mode</h3>
          <span class="px-2 py-1 bg-purple-600/30 text-purple-300 text-xs rounded-full font-mono">v0.3</span>
        </div>
        <div class="flex items-center gap-4">
          <div class="text-center">
            <div class="text-2xl font-bold text-yellow-400">${this.state.you.score}</div>
            <div class="text-xs text-gray-400">Score</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-blue-400">Level ${this.state.you.level}</div>
            <div class="text-xs text-gray-400">Level</div>
          </div>
        </div>
      </div>
      <div class="text-sm text-gray-300">
        Manage your insurance pool strategically. Make decisions that affect premiums, claims, and pool health.
        <span class="text-green-400 font-medium">Earn points</span> by maintaining pool stability!
      </div>
    `;

    // Insert the game header right before the canvas container
    canvasContainer.insertAdjacentElement('beforebegin', gameHeader);
    console.log('🎯 Game header inserted successfully before canvas');
  }

  addScenarioSelector() {
    console.log('🎲 Adding scenario selector');

    // Find the game header we just inserted and add scenario selector after it
    const gameHeader = document.querySelector('#game-header-v0-3');
    if (!gameHeader) {
      console.log('🎲 ERROR: Game header not found, cannot insert scenario selector');
      return;
    }

    const scenarios = [
      { id: 'normal', name: 'Normal Year', description: 'Balanced market', icon: '🌤️' },
      { id: 'storm', name: 'Natural Disaster', description: 'High claims', icon: '🌪️' },
      { id: 'recession', name: 'Economic Downturn', description: 'Reduced premiums', icon: '📉' },
      { id: 'boom', name: 'Economic Boom', description: 'Growing premiums', icon: '🚀' }
    ];

    const scenarioContainer = document.createElement('div');
    scenarioContainer.id = 'scenario-selector-v0-3';
    scenarioContainer.className = 'mb-6 p-4 bg-gray-800/30 rounded-lg border border-gray-600/30';
    scenarioContainer.innerHTML = `
      <h4 class="text-lg font-semibold text-white mb-3 flex items-center gap-2">
        <span>🎲</span> Choose Scenario (Coming Soon)
      </h4>
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 opacity-60">
        ${scenarios.map(scenario => `
          <div class="p-3 rounded-lg border-2 border-gray-600 bg-gray-800/50 text-center">
            <div class="text-xl mb-1">${scenario.icon}</div>
            <div class="font-semibold text-white text-sm">${scenario.name}</div>
            <div class="text-xs text-gray-400">${scenario.description}</div>
          </div>
        `).join('')}
      </div>
      <div class="text-xs text-gray-500 mt-2 text-center">Interactive scenarios will be enabled in the next update</div>
    `;

    // Insert right after the game header
    gameHeader.insertAdjacentElement('afterend', scenarioContainer);
    console.log('🎲 Scenario selector inserted after game header');
  }
}


